import type { GeneratedFunnelTemplate } from "schemas/generated/funnelTemplate";
import type { TemplateItem } from "../NewFunnelPage";

import React, { useState } from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import IconButton from "@replo/design-system/components/button/IconButton";
import SelectableCard from "@replo/design-system/components/card/SelectableCard";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import {
  ClipboardCheck,
  GraduationCap,
  MessageSquareQuote,
  MousePointerClick,
  Sparkles,
} from "lucide-react";

import FunnelTemplateCard, {
  FunnelTemplateCardSkeleton,
} from "./FunnelTemplateCard";
import GeneratedFunnelTemplateCard, {
  GeneratedFunnelTemplateCardSkeleton,
} from "./GeneratedFunnelTemplateCard";

type GoalCategory = "awareness" | "convert" | "educate" | "lead-capture";

const goals = {
  awareness: {
    label: "Awareness",
    description: "Raise customer awareness",
    // eslint-disable-next-line replo/no-tailwind-literal-color
    icon: <ClipboardCheck size={12} className="text-pink-600" />,
    colors: {
      bg: "bg-pink-50",
      border: "border-pink-300",
      hover: "hover:bg-pink-50",
    },
  },
  convert: {
    label: "Convert",
    description: "Turn visitors into customers",
    // eslint-disable-next-line replo/no-tailwind-literal-color
    icon: <MousePointerClick size={12} className="text-orange-600" />,
    colors: {
      bg: "bg-orange-50",
      border: "border-orange-300",
      hover: "hover:bg-orange-50",
    },
  },
  educate: {
    label: "Educate",
    description: "Clarify product value fast",
    // eslint-disable-next-line replo/no-tailwind-literal-color
    icon: <GraduationCap size={12} className="text-lime-600" />,
    colors: {
      bg: "bg-lime-50",
      border: "border-lime-300",
      hover: "hover:bg-lime-50",
    },
  },
  "lead-capture": {
    label: "Lead Capture",
    description: "Collect data to re-engage",
    // eslint-disable-next-line replo/no-tailwind-literal-color
    icon: <MessageSquareQuote size={12} className="text-cyan-600" />,
    colors: {
      bg: "bg-cyan-50",
      border: "border-cyan-300",
      hover: "hover:bg-cyan-50",
    },
  },
} as const;

function GoalCard({
  goalId,
  isActive,
  onClick,
}: {
  goalId: GoalCategory;
  isActive: boolean;
  onClick: () => void;
}) {
  const goal = goals[goalId];

  return (
    <SelectableCard
      size="sm"
      startEnhancer={
        <div
          className={twMerge(
            "p-1.5 rounded-sm w-fit shadow-selectable border-0.5",
            isActive ? "bg-white" : goal.colors.bg,
            goal.colors.border,
          )}
        >
          {goal.icon}
        </div>
      }
      title={goal.label}
      description={goal.description}
      UNSAFE_className={twMerge(
        isActive && `${goal.colors.bg} ${goal.colors.border}`,
        goal.colors.hover,
      )}
      contentClassName="p-2 gap-3 flex flex-row items-center"
      descriptionSize="xs"
      buttonClassName="flex"
      isActive={isActive}
      onClick={onClick}
    />
  );
}

export default function FunnelMenu({
  selectedFunnelTemplate,
  setSelectedFunnelTemplate,
}: {
  selectedFunnelTemplate: TemplateItem | null;
  setSelectedFunnelTemplate: (template: TemplateItem | null) => void;
}) {
  const projectId = useCurrentProjectId();

  const [selectedGoal, setSelectedGoal] = useState<GoalCategory | null>(null);
  const [searchString, setSearchString] = useState("");
  const [generatedFunnelTemplates, setGeneratedFunnelTemplates] = useState<
    GeneratedFunnelTemplate[]
  >([]);

  const { data: funnelTemplates } = trpc.funnelTemplate.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const {
    mutate: generateFunnelTemplates,
    isPending: isGeneratingFunnelTemplates,
  } = trpc.funnelTemplate.generateFromPrompt.useMutation({
    onSuccess: (generatedFunnelTemplates) => {
      setGeneratedFunnelTemplates(generatedFunnelTemplates);
    },
  });

  const handleTemplateClick = (templateId: string) => {
    const template =
      funnelTemplates?.find((t) => t.id === templateId) ??
      generatedFunnelTemplates.find((t) => t.id === templateId);

    if (selectedFunnelTemplate?.id === templateId) {
      setSelectedFunnelTemplate(null);
    } else if (template) {
      setSelectedFunnelTemplate(template);
    }
  };

  const handleGenerateFunnel = () => {
    if (!projectId || !searchString.trim()) {
      return;
    }

    setGeneratedFunnelTemplates([]);
    setSelectedFunnelTemplate(null);
    generateFunnelTemplates({
      projectId,
      prompt: searchString.trim(),
      numberOfFunnels: 2,
    });
  };

  const showGeneratedFunnelTemplatesSection =
    generatedFunnelTemplates.length > 0 || isGeneratingFunnelTemplates;

  return (
    <div className="flex flex-col h-full gap-6 no-scrollbar">
      <DebouncedInput
        placeholder="Search templates or tell us what you want..."
        size="base"
        value={searchString}
        onValueChange={setSearchString}
        endEnhancer={() => (
          <IconButton
            size="sm"
            variant="primary"
            icon={<Sparkles size={12} />}
            isLoading={isGeneratingFunnelTemplates}
            disabled={!searchString.trim() || isGeneratingFunnelTemplates}
            onClick={handleGenerateFunnel}
          />
        )}
        onKeyDown={(e) => {
          if (
            e.key === "Enter" &&
            !isGeneratingFunnelTemplates &&
            Boolean(searchString.trim())
          ) {
            handleGenerateFunnel();
          }
        }}
      />

      {showGeneratedFunnelTemplatesSection && (
        // eslint-disable-next-line replo/no-tailwind-literal-color
        <div className="bg-indigo-50 p-4 rounded-lg flex flex-col gap-3">
          <div className="flex items-center gap-1.5 header-h4 text-muted">
            <Sparkles size={16} />
            <span>Generated</span>
          </div>

          <div className="flex flex-col gap-2">
            {isGeneratingFunnelTemplates
              ? Array.from({ length: 2 }).map((_, index) => (
                  <GeneratedFunnelTemplateCardSkeleton key={index} />
                ))
              : generatedFunnelTemplates.map((template) => (
                  <GeneratedFunnelTemplateCard
                    key={template.id}
                    template={template}
                    onClick={() => handleTemplateClick(template.id)}
                    isSelected={selectedFunnelTemplate?.id === template.id}
                  />
                ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <h4 className="header-h4">Browse by goal</h4>

        {/* TODO (Ben O, 2025-06-24, REPL-17697): Make the goal filtering actually work */}
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(goals) as GoalCategory[]).map((goalId) => (
            <GoalCard
              key={goalId}
              goalId={goalId}
              isActive={selectedGoal === goalId}
              onClick={() =>
                setSelectedGoal(selectedGoal === goalId ? null : goalId)
              }
            />
          ))}
        </div>

        <div className="flex flex-col gap-4 mb-20">
          {funnelTemplates
            ? funnelTemplates?.map((template) => (
                <FunnelTemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedFunnelTemplate?.id === template.id}
                  onClick={() => handleTemplateClick(template.id)}
                />
              ))
            : Array.from({ length: 4 }).map((_, index) => (
                <FunnelTemplateCardSkeleton key={index} />
              ))}
        </div>
      </div>
    </div>
  );
}
